import React from 'react';
import { IconButton, TableSortLabel, Typography } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import type { ElasticSearchFilters } from 'c-sdk';
import { EsPCAReportField, EsPCAReportFilterField } from 'c-sdk';
import type { TableReportCell, TableReportHeader, TableReportRow } from 'c-reports/Types';
import type { AnalyticsLeaderboardDataRow } from './energyUsageUtils';
import {
    getAverageAudienceVals,
    getGeoVals,
    getRowFieldValue,
    parseNum,
    pickPlays,
} from './energyUsageUtils';
import type { DrilldownKey } from '../AnalyticsHourlyHistogramDialog';

const nf0 = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
const fmtWhole = (n?: number | null) => nf0.format(Math.round(n ?? 0));

const cell = (content: string | number | null | undefined): TableReportCell => ({
    content: content ?? '-',
});

export type AnalyticsLeaderboardSortKey =
    | 'field'
    | 'displayFormat'
    | 'dspImpressions'
    | 'geoImpressions'
    | 'variance'
    | 'performance'
    | 'dspIm'
    | 'geoIm'
    | 'avgIm'
    | 'imVar'
    | 'pctVar'
    | 'totalPlays'
    | 'totalImpressions'
    | 'avgDspIm'
    | null;

export type AnalyticsLeaderboardSortDir = 'asc' | 'desc';

type Translate = (path: string, options?: Record<string, any>) => string;

type HeaderArgs = {
    t: Translate;
    field: EsPCAReportField;
    showRoute: boolean;
    showDisplayFormatColumn: boolean;
    geopathOnly: boolean;
    isAverageAudienceMode: boolean;
    sortKey: AnalyticsLeaderboardSortKey;
    sortDir: AnalyticsLeaderboardSortDir;
    showAuditDspColumns: boolean;
    hasAuditedAudience: boolean;
    selectedAudienceLabel?: string | null;
    showNonAuditedDspColumns: boolean;
    toggleSort: (key: Exclude<AnalyticsLeaderboardSortKey, null>) => void;
    isOwnerFormatGroup: boolean;
    groupFields?: EsPCAReportField[];
};

const buildGroupHeaders = (
    fields: EsPCAReportField[],
    t: Translate,
    sortKey: AnalyticsLeaderboardSortKey,
    sortDir: AnalyticsLeaderboardSortDir,
    toggleSort: (key: Exclude<AnalyticsLeaderboardSortKey, null>) => void,
): TableReportHeader[] =>
    fields.map((f, idx) => {
        const label = t(`Modules.Main.Campaigns.Overview.Reports.fields.${f}`);
        const key: Exclude<AnalyticsLeaderboardSortKey, null> | null =
            idx === 0 ? 'field' : idx === 1 ? 'displayFormat' : null;
        if (key == null) return { content: label };
        return {
            content: (
                <TableSortLabel
                    active={sortKey === key}
                    direction={sortKey === key ? sortDir : 'desc'}
                    onClick={() => toggleSort(key)}
                >
                    {label}
                </TableSortLabel>
            ),
        };
    });

const buildGroupCells = (row: any, fields: EsPCAReportField[]): TableReportCell[] =>
    fields.map(f => cell(String(getRowFieldValue(row, f) ?? '-')));

const getAnalyticsLeaderboardLabels = ({
    t,
    field,
    showRoute,
    hasAuditedAudience,
    selectedAudienceLabel,
    isOwnerFormatGroup,
}: Pick<
    HeaderArgs,
    | 't'
    | 'field'
    | 'showRoute'
    | 'hasAuditedAudience'
    | 'selectedAudienceLabel'
    | 'isOwnerFormatGroup'
>) => {
    const fieldLabel = isOwnerFormatGroup
        ? t(`Modules.Main.Campaigns.Overview.Reports.fields.${EsPCAReportField.DisplayOwner}`)
        : t(`Modules.Main.Campaigns.Overview.Reports.fields.${field}`);
    const displayFormatLabel = t('Modules.Main.Campaigns.Overview.Analytics.displayFormatLabel');
    const totalPlaysLabel = t('Modules.Main.Campaigns.Overview.Analytics.totalPlays');
    const dspImLabel = t('Modules.Main.Campaigns.Overview.dsp.dspIm');
    const dspImpressionsLabel = t('Modules.Main.Campaigns.Overview.dsp.dspImpressions');
    const dspAverageImpactsLabel = t('Modules.Main.Campaigns.Overview.Analytics.dspAverageImpacts');
    const geopathImpressionsLabel = showRoute
        ? t('Modules.Main.Campaigns.Overview.dsp.routeImpressions')
        : t('Modules.Main.Campaigns.Overview.dsp.geopathImpressions');
    const varianceLabel = t('Modules.Main.Campaigns.Overview.Analytics.varianceLabel');
    const auditedVarianceLabel = hasAuditedAudience
        ? t('Modules.Main.Campaigns.Overview.dsp.var2')
        : varianceLabel;
    const performanceLabel = t('Modules.Main.Campaigns.Overview.Analytics.performanceLabel');
    const auditedAudienceImpactsLabel = selectedAudienceLabel
        ? `${selectedAudienceLabel} ${t('Modules.Main.Campaigns.Overview.Analytics.impactsTitle')}`
        : null;
    const auditImpressionsLabel = auditedAudienceImpactsLabel ?? geopathImpressionsLabel;
    const auditImLabel = selectedAudienceLabel
        ? `${selectedAudienceLabel} VAR`
        : showRoute
        ? t('Modules.Main.Campaigns.Overview.dsp.routeIm')
        : t('Modules.Main.Campaigns.Overview.dsp.geopathIm');

    return {
        fieldLabel,
        displayFormatLabel,
        totalPlaysLabel,
        dspImLabel,
        dspImpressionsLabel,
        dspAverageImpactsLabel,
        geopathImpressionsLabel,
        varianceLabel,
        auditedVarianceLabel,
        performanceLabel,
        auditImpressionsLabel,
        auditImLabel,
    };
};

export const buildAnalyticsLeaderboardHeaders = ({
    t,
    field,
    showRoute,
    showDisplayFormatColumn,
    geopathOnly,
    isAverageAudienceMode,
    sortKey,
    sortDir,
    showAuditDspColumns,
    hasAuditedAudience,
    selectedAudienceLabel,
    showNonAuditedDspColumns,
    toggleSort,
    isOwnerFormatGroup,
    groupFields,
}: HeaderArgs): TableReportHeader[] => {
    const {
        fieldLabel,
        displayFormatLabel,
        totalPlaysLabel,
        dspImLabel,
        dspImpressionsLabel,
        dspAverageImpactsLabel,
        geopathImpressionsLabel,
        varianceLabel,
        auditedVarianceLabel,
        performanceLabel,
        auditImpressionsLabel,
        auditImLabel,
    } = getAnalyticsLeaderboardLabels({
        t,
        field,
        showRoute,
        hasAuditedAudience,
        selectedAudienceLabel,
        isOwnerFormatGroup,
    });

    const isMultiGroup = Array.isArray(groupFields) && groupFields.length > 1;

    const sortableHeader = (
        label: string,
        key: Exclude<AnalyticsLeaderboardSortKey, null>,
        sortable = true,
    ): TableReportHeader => ({
        content: sortable ? (
            <TableSortLabel
                active={sortKey === key}
                direction={sortKey === key ? sortDir : 'desc'}
                onClick={() => toggleSort(key)}
            >
                {label}
            </TableSortLabel>
        ) : (
            label
        ),
    });

    const groupHeadersPrefix: TableReportHeader[] = isMultiGroup
        ? buildGroupHeaders(groupFields as EsPCAReportField[], t, sortKey, sortDir, toggleSort)
        : [
              sortableHeader(fieldLabel, 'field'),
              ...(showDisplayFormatColumn
                  ? [sortableHeader(displayFormatLabel, 'displayFormat')]
                  : []),
          ];

    const geopathAuditBase = [
        ...groupHeadersPrefix,
        sortableHeader(totalPlaysLabel, 'totalPlays'),
        sortableHeader(t('Modules.Main.Campaigns.Overview.dsp.dspImpressions'), 'dspImpressions'),
        sortableHeader(auditImpressionsLabel, 'geoImpressions'),
        sortableHeader(t('Modules.Main.Campaigns.Overview.dsp.var2'), 'variance'),
        sortableHeader(t('Modules.Main.Campaigns.Overview.dsp.dspIm'), 'dspIm'),
        sortableHeader(auditImLabel, 'geoIm'),
        sortableHeader(t('Modules.Main.Campaigns.Overview.dsp.imVar'), 'imVar'),
        sortableHeader(t('Modules.Main.Campaigns.Overview.dsp.var'), 'pctVar'),
    ];
    const nonAuditedBase = [
        ...groupHeadersPrefix,
        sortableHeader(totalPlaysLabel, 'totalPlays'),
        sortableHeader(t('Modules.Main.Campaigns.Overview.dsp.dspImpressions'), 'dspImpressions'),
        sortableHeader(t('Modules.Main.Campaigns.Overview.dsp.dspIm'), 'dspIm'),
        sortableHeader(t('Modules.Main.Campaigns.Overview.dsp.averageFormatIm'), 'avgIm'),
        sortableHeader(t('Modules.Main.Campaigns.Overview.dsp.imVar'), 'imVar'),
        sortableHeader(t('Modules.Main.Campaigns.Overview.dsp.var'), 'pctVar'),
    ];

    const base = geopathOnly
        ? [
              ...groupHeadersPrefix,
              sortableHeader(totalPlaysLabel, 'totalPlays'),
              sortableHeader(dspImpressionsLabel, 'dspImpressions'),
              sortableHeader(auditImpressionsLabel, 'geoImpressions'),
              sortableHeader(auditedVarianceLabel, 'variance'),
              sortableHeader(performanceLabel, 'performance'),
          ]
        : isAverageAudienceMode
        ? [
              ...groupHeadersPrefix,
              sortableHeader(totalPlaysLabel, 'totalPlays'),
              sortableHeader(dspImpressionsLabel, 'dspImpressions'),
              sortableHeader(dspAverageImpactsLabel, 'geoImpressions'),
              sortableHeader(varianceLabel, 'variance'),
              sortableHeader(performanceLabel, 'performance'),
          ]
        : [
              ...groupHeadersPrefix,
              sortableHeader(totalPlaysLabel, 'totalPlays'),
              sortableHeader(dspImpressionsLabel, 'totalImpressions'),
              sortableHeader(dspImLabel, 'avgDspIm'),
          ];

    const columns = showAuditDspColumns
        ? geopathAuditBase
        : showNonAuditedDspColumns
        ? nonAuditedBase
        : base;

    return [{ content: '' }, ...columns];
};

export const buildAnalyticsLeaderboardExportData = ({
    t,
    field,
    showRoute,
    showDisplayFormatColumn,
    geopathOnly,
    isAverageAudienceMode,
    showAuditDspColumns,
    hasAuditedAudience,
    selectedAudienceLabel,
    showNonAuditedDspColumns,
    isOwnerFormatGroup,
    sortedRowsData,
    groupFields,
}: Omit<HeaderArgs, 'sortKey' | 'sortDir' | 'toggleSort'> & {
    sortedRowsData: AnalyticsLeaderboardDataRow[];
}) => {
    const isMultiGroup = Array.isArray(groupFields) && groupFields.length > 1;
    return sortedRowsData.map((stats, idx) => {
        const {
            fieldLabel,
            displayFormatLabel,
            totalPlaysLabel,
            dspImLabel,
            dspImpressionsLabel,
            dspAverageImpactsLabel,
            geopathImpressionsLabel,
            auditedVarianceLabel,
            performanceLabel,
            auditImpressionsLabel,
            auditImLabel,
        } = getAnalyticsLeaderboardLabels({
            t,
            field,
            showRoute,
            hasAuditedAudience,
            selectedAudienceLabel,
            isOwnerFormatGroup,
        });
        const rawFieldValue = getRowFieldValue(stats, field);
        const fieldValue = rawFieldValue == null || rawFieldValue === '' ? '-' : rawFieldValue;
        const rawDisplayFormat =
            stats.display_format ??
            stats.display_format_name ??
            stats.displayFormat ??
            stats.format ??
            '-';
        const ownerLabel =
            stats.display_owner ?? stats.displayOwner ?? stats.owner ?? String(fieldValue);
        const baseRow: Record<string, string> = {};
        if (isMultiGroup) {
            (groupFields as EsPCAReportField[]).forEach(f => {
                const label = t(`Modules.Main.Campaigns.Overview.Reports.fields.${f}`);
                baseRow[label] = String(getRowFieldValue(stats, f) ?? '-');
            });
        } else {
            baseRow[fieldLabel] = String(isOwnerFormatGroup ? ownerLabel : fieldValue);
            if (showDisplayFormatColumn) {
                baseRow[displayFormatLabel] = String(rawDisplayFormat);
            }
        }

        const totalPlaysVal = fmtWhole(Number(pickPlays(stats)));

        if (isAverageAudienceMode && !showAuditDspColumns && !showNonAuditedDspColumns) {
            const { dsp, avg, variance, performancePct } = getAverageAudienceVals(stats);
            return {
                ...baseRow,
                [totalPlaysLabel]: totalPlaysVal,
                [dspImpressionsLabel]: fmtWhole(dsp),
                [dspAverageImpactsLabel]: fmtWhole(avg),
                [auditedVarianceLabel]: `${variance > 0 ? '+' : ''}${fmtWhole(variance)}`,
                [performanceLabel]: `${Math.round(performancePct)}%`,
            };
        }

        if (showAuditDspColumns) {
            const playsForRow = Number(pickPlays(stats)) || 0;
            const dspImpressions = parseNum(
                stats.dsp_impressions ?? stats.impacts ?? stats.impressions ?? 0,
            );
            const geoImpressions = parseNum(
                stats.geopath_impressions ?? stats.geopath_audience ?? 0,
            );
            const varianceDirect = parseNum(stats.variance ?? geoImpressions - dspImpressions);
            const dspIm = parseNum(
                stats.dsp_avg_impressions ?? (playsForRow > 0 ? dspImpressions / playsForRow : 0),
            );
            const geoIm = parseNum(
                stats.geopath_avg_impressions ??
                    (playsForRow > 0 ? geoImpressions / playsForRow : 0),
            );
            const imVar = parseNum(stats.im_variance ?? geoIm - dspIm);
            const pctVar =
                parseNum(
                    stats.variance_percent ??
                        (dspImpressions > 0 ? varianceDirect / dspImpressions : 0),
                ) * 100;

            return {
                ...baseRow,
                [totalPlaysLabel]: totalPlaysVal,
                [dspImpressionsLabel]: fmtWhole(dspImpressions),
                [auditImpressionsLabel]: fmtWhole(geoImpressions),
                [t('Modules.Main.Campaigns.Overview.dsp.var2')]: `${
                    varianceDirect > 0 ? '+' : ''
                }${fmtWhole(varianceDirect)}`,
                [dspImLabel]: dspIm.toFixed(2),
                [auditImLabel]: geoIm.toFixed(2),
                [t('Modules.Main.Campaigns.Overview.dsp.imVar')]: imVar.toFixed(2),
                [t('Modules.Main.Campaigns.Overview.dsp.var')]: `${pctVar.toFixed(0)} %`,
            };
        }

        if (showNonAuditedDspColumns) {
            const playsForRow = Number(pickPlays(stats)) || 0;
            const dspImpressions = parseNum(
                stats.dsp_impressions ?? stats.impacts ?? stats.impressions ?? 0,
            );
            const dspIm = parseNum(
                stats.dsp_avg_impressions ?? (playsForRow > 0 ? dspImpressions / playsForRow : 0),
            );
            const avgIm = parseNum(stats.average_im ?? dspIm);
            const imVar = parseNum(stats.variance ?? stats.im_variance ?? dspIm - avgIm);
            const pctVar =
                parseNum(stats.variance_percent ?? (avgIm > 0 ? (dspIm - avgIm) / avgIm : 0)) * 100;

            return {
                ...baseRow,
                [totalPlaysLabel]: totalPlaysVal,
                [dspImpressionsLabel]: fmtWhole(dspImpressions),
                [dspImLabel]: dspIm.toFixed(2),
                [t('Modules.Main.Campaigns.Overview.dsp.averageFormatIm')]: avgIm.toFixed(2),
                [t('Modules.Main.Campaigns.Overview.dsp.imVar')]: imVar.toFixed(2),
                [t('Modules.Main.Campaigns.Overview.dsp.var')]: `${pctVar.toFixed(0)} %`,
            };
        }

        if (geopathOnly) {
            const { dsp, geo, variance, performancePct } = getGeoVals(stats);
            return {
                ...baseRow,
                [totalPlaysLabel]: totalPlaysVal,
                [dspImpressionsLabel]: fmtWhole(dsp),
                [geopathImpressionsLabel]: fmtWhole(geo),
                [auditedVarianceLabel]: `${variance > 0 ? '+' : ''}${fmtWhole(variance)}`,
                [performanceLabel]: `${Math.round(performancePct)}%`,
            };
        }

        const totalPlays = pickPlays(stats);
        const totalImpressions = stats.impacts ?? stats.impressions ?? 0;
        const avgDspIm =
            parseNum(totalPlays) > 0 ? parseNum(totalImpressions) / parseNum(totalPlays) : 0;

        return {
            ...baseRow,
            [totalPlaysLabel]: fmtWhole(Number(totalPlays)),
            [dspImpressionsLabel]: fmtWhole(Number(totalImpressions)),
            [dspImLabel]: avgDspIm.toFixed(2),
        };
    });
};

type RowArgs = {
    sortedRowsData: AnalyticsLeaderboardDataRow[];
    field: EsPCAReportField;
    geopathOnly: boolean;
    isAverageAudienceMode: boolean;
    isOwnerFormatGroup: boolean;
    showDisplayFormatColumn: boolean;
    showAuditDspColumns: boolean;
    showNonAuditedDspColumns: boolean;
    useMultiplierTable: boolean;
    setDrilldown: React.Dispatch<React.SetStateAction<DrilldownKey | null>>;
    groupFields?: EsPCAReportField[];
};

export const buildAnalyticsLeaderboardRows = ({
    sortedRowsData,
    field,
    geopathOnly,
    isAverageAudienceMode,
    isOwnerFormatGroup,
    showDisplayFormatColumn,
    showAuditDspColumns,
    showNonAuditedDspColumns,
    useMultiplierTable,
    setDrilldown,
    groupFields,
}: RowArgs): TableReportRow[] => {
    const isMultiGroup = Array.isArray(groupFields) && groupFields.length > 1;
    return sortedRowsData.map((r, idx) => {
        const stats = r;
        const fieldValue = getRowFieldValue(r, field) || '-';
        const rawDisplayFormat =
            stats.display_format ??
            stats.display_format_name ??
            stats.displayFormat ??
            stats.format ??
            '-';

        const ownerLabel = stats.display_owner ?? stats.displayOwner ?? stats.owner ?? fieldValue;
        const groupValue = String(fieldValue);
        const displayFormat = rawDisplayFormat;
        const groupCellsPrefix: TableReportCell[] = isMultiGroup
            ? buildGroupCells(r, groupFields as EsPCAReportField[])
            : [
                  cell(isOwnerFormatGroup ? String(ownerLabel) : String(fieldValue)),
                  ...(showDisplayFormatColumn ? [cell(String(displayFormat))] : []),
              ];
        const ownerFormatStatsFilters: ElasticSearchFilters | undefined = isMultiGroup
            ? ((groupFields as EsPCAReportField[]).reduce((acc, f) => {
                  const v = getRowFieldValue(r, f);
                  if (v != null && String(v).trim() !== '') {
                      (acc as any)[f as string] = [String(v).trim()];
                  }
                  return acc;
              }, {} as ElasticSearchFilters) as ElasticSearchFilters)
            : isOwnerFormatGroup
            ? {
                  [EsPCAReportFilterField.DisplayOwner]: [String(ownerLabel).trim()],
                  [EsPCAReportFilterField.DisplayFormat]: [String(displayFormat).trim()],
              }
            : undefined;

        const actionCell: TableReportCell = {
            content: (
                <IconButton
                    size="small"
                    onClick={() =>
                        setDrilldown({
                            field,
                            filterField: field as any as EsPCAReportFilterField,
                            value: groupValue,
                            statsFilters: ownerFormatStatsFilters,
                        })
                    }
                >
                    <InfoOutlined fontSize="small" />
                </IconButton>
            ),
        };

        const totalPlaysCell = cell(fmtWhole(Number(pickPlays(stats))));
        if (useMultiplierTable || geopathOnly || isAverageAudienceMode) {
            if (isAverageAudienceMode && !showAuditDspColumns && !showNonAuditedDspColumns) {
                const { dsp, avg, variance, performancePct } = getAverageAudienceVals(stats);
                const varianceColor = variance >= 0 ? 'success.main' : 'error.main';
                const perfColor = performancePct >= 100 ? 'success.main' : 'error.main';

                return {
                    key: `${fieldValue}-${idx}`,
                    content: [
                        actionCell,
                        ...groupCellsPrefix,
                        totalPlaysCell,
                        cell(fmtWhole(dsp)),
                        cell(fmtWhole(avg)),
                        {
                            content: (
                                <Typography variant="body2" color={varianceColor} noWrap>
                                    {`${variance > 0 ? '+' : ''}${fmtWhole(variance)}`}
                                </Typography>
                            ),
                        },
                        {
                            content: (
                                <Typography variant="body2" color={perfColor} noWrap>
                                    {`${Math.round(performancePct)}%`}
                                </Typography>
                            ),
                        },
                    ],
                };
            }

            const { dsp, geo, variance, performancePct } = getGeoVals(stats);
            const varianceColor = variance >= 0 ? 'success.main' : 'error.main';
            const perfColor = performancePct >= 100 ? 'success.main' : 'error.main';

            if (showAuditDspColumns) {
                const playsForRow = Number(pickPlays(stats)) || 0;
                const dspImpressions = parseNum(
                    stats.dsp_impressions ?? stats.impacts ?? stats.impressions ?? 0,
                );
                const geoImpressions = parseNum(
                    stats.geopath_impressions ?? stats.geopath_audience ?? 0,
                );
                const varianceDirect = parseNum(stats.variance ?? geoImpressions - dspImpressions);
                const dspIm = parseNum(
                    stats.dsp_avg_impressions ??
                        (playsForRow > 0 ? dspImpressions / playsForRow : 0),
                );
                const geoIm = parseNum(
                    stats.geopath_avg_impressions ??
                        (playsForRow > 0 ? geoImpressions / playsForRow : 0),
                );
                const imVar = parseNum(stats.im_variance ?? geoIm - dspIm);
                const pctVar =
                    parseNum(
                        stats.variance_percent ??
                            (dspImpressions > 0 ? varianceDirect / dspImpressions : 0),
                    ) * 100;
                const imVarColor =
                    imVar === 0 ? 'text.primary' : imVar > 0 ? 'success.main' : 'error.main';
                const pctVarColor =
                    pctVar === 0 ? 'text.primary' : pctVar > 0 ? 'success.main' : 'error.main';

                return {
                    key: `${fieldValue}-${idx}`,
                    content: [
                        actionCell,
                        ...groupCellsPrefix,
                        totalPlaysCell,
                        cell(fmtWhole(dspImpressions)),
                        cell(fmtWhole(geoImpressions)),
                        {
                            content: (
                                <Typography variant="body2" color={varianceColor} noWrap>
                                    {`${varianceDirect > 0 ? '+' : ''}${fmtWhole(varianceDirect)}`}
                                </Typography>
                            ),
                        },
                        cell(dspIm.toFixed(2)),
                        cell(geoIm.toFixed(2)),
                        {
                            content: (
                                <Typography variant="body2" color={imVarColor} noWrap>
                                    {imVar.toFixed(2)}
                                </Typography>
                            ),
                        },
                        {
                            content: (
                                <Typography variant="body2" color={pctVarColor} noWrap>
                                    {`${pctVar.toFixed(0)} %`}
                                </Typography>
                            ),
                        },
                    ],
                };
            }

            if (showNonAuditedDspColumns) {
                const playsForRow = Number(pickPlays(stats)) || 0;
                const dspImpressions = parseNum(
                    stats.dsp_impressions ?? stats.impacts ?? stats.impressions ?? 0,
                );
                const dspIm = parseNum(
                    stats.dsp_avg_impressions ??
                        (playsForRow > 0 ? dspImpressions / playsForRow : 0),
                );
                const avgIm = parseNum(stats.average_im ?? dspIm);
                const imVar = parseNum(stats.variance ?? stats.im_variance ?? dspIm - avgIm);
                const pctVar =
                    parseNum(stats.variance_percent ?? (avgIm > 0 ? (dspIm - avgIm) / avgIm : 0)) *
                    100;
                const imVarColor =
                    imVar === 0 ? 'text.primary' : imVar > 0 ? 'success.main' : 'error.main';
                const pctVarColor =
                    pctVar === 0 ? 'text.primary' : pctVar > 0 ? 'success.main' : 'error.main';

                return {
                    key: `${fieldValue}-${idx}`,
                    content: [
                        actionCell,
                        ...groupCellsPrefix,
                        totalPlaysCell,
                        cell(fmtWhole(dspImpressions)),
                        cell(dspIm.toFixed(2)),
                        cell(avgIm.toFixed(2)),
                        {
                            content: (
                                <Typography variant="body2" color={imVarColor} noWrap>
                                    {imVar.toFixed(2)}
                                </Typography>
                            ),
                        },
                        {
                            content: (
                                <Typography variant="body2" color={pctVarColor} noWrap>
                                    {`${pctVar.toFixed(0)} %`}
                                </Typography>
                            ),
                        },
                    ],
                };
            }

            return {
                key: `${fieldValue}-${idx}`,
                content: [
                    actionCell,
                    ...groupCellsPrefix,
                    totalPlaysCell,
                    cell(fmtWhole(dsp)),
                    cell(fmtWhole(geo)),
                    {
                        content: (
                            <Typography variant="body2" color={varianceColor} noWrap>
                                {`${variance > 0 ? '+' : ''}${fmtWhole(variance)}`}
                            </Typography>
                        ),
                    },
                    {
                        content: (
                            <Typography variant="body2" color={perfColor} noWrap>
                                {`${Math.round(performancePct)}%`}
                            </Typography>
                        ),
                    },
                ],
            };
        }

        const totalPlays = stats.sum_of_plays ?? stats.plays ?? 0;
        const totalImpressions = stats.impacts ?? stats.impressions ?? 0;
        const avgDspIm =
            parseNum(totalPlays) > 0 ? parseNum(totalImpressions) / parseNum(totalPlays) : 0;

        return {
            key: `${fieldValue}-${idx}`,
            content: [
                actionCell,
                ...groupCellsPrefix,
                cell(fmtWhole(Number(totalPlays))),
                cell(fmtWhole(Number(totalImpressions))),
                cell(avgDspIm.toFixed(2)),
            ],
        };
    });
};
